<template>
  <el-dialog
    v-model="visible"
    title="Создание смены"
    class="create-schedule-dialog"
    :width="dialogWidth"
    align-center
    @close="resetForm"
  >
    <el-form :model="form" ref="formRef" :label-width="formLabelWidth" hide-required-asterisk>
      <el-form-item label="*Сотрудник" prop="user_id" :rules="[{ required: true, message: 'Выберите сотрудника', trigger: 'change' }]">
        <el-select v-model="form.user_id" placeholder="Выберите">
          <el-option
            v-for="user in employees"
            :key="user.id"
            :label="user.name"
            :value="user.id"
          />
        </el-select>
        <el-alert
          v-if="userError"
          type="error"
          show-icon
          :closable="false"
          class="employee-error-alert"
        >
          <template #title>
            {{ userError }}
            <el-link
              type="primary"
              style="margin-left: 6px;"
              @click="$router.push('/employees')"
            >
              Перейти к настройке доступа
            </el-link>
          </template>
        </el-alert>
      </el-form-item>

      <el-form-item
        v-if="hasAssignedLocations"
        label="Локация"
        prop="location_id"
      >
        <el-select v-model="form.location_id" clearable placeholder="Без локации" :disabled="!form.user_id || !!userError">
          <el-option
            v-for="location in filteredLocations"
            :key="location.id"
            :label="location.name"
            :value="location.id"
            :disabled="isLocationUsed(location)"
          />
        </el-select>
      </el-form-item>

      <el-form-item label="Шаблон смены">
        <div class="template-actions">
          <el-button
            class="template-apply-btn"
            @click="applyTemplateDialogVisible = true"
            :disabled="!form.user_id"
          >
            Применить шаблон
          </el-button>
        </div>
      </el-form-item>
    </el-form>
    <shift-calendar
      v-model:shifts="generatedShifts"
      mode="edit"
      :workingHours="workingHours"
      :unrestricted-hours="!form.location_id"
      :disabled="!form.user_id"
    />

    <template #footer>
      <el-button class="dialog-footer-btn dialog-footer-btn-cancel" @click="visible = false">Отмена</el-button>
      <el-button class="dialog-footer-btn dialog-footer-btn-submit" type="primary" @click="submit">Создать</el-button>
    </template>
  </el-dialog>



  <el-dialog
    v-model="applyTemplateDialogVisible"
    title="Применить шаблон"
    width="320"
    class="apply-template-dialog"
  >
    <el-select v-model="selectedTemplate" placeholder="Выберите шаблон" style="width: 100%; margin-bottom: 12px;">
      <el-option
        v-for="template in templates"
        :key="template.id"
        :label="template.name"
        :value="template.id"
      />
      <template #empty>
        <div class="empty-template-select">
          <span>Шаблонов пока нет</span>
          <el-button type="primary" link @click="createFirstTemplate">Создать шаблон</el-button>
        </div>
      </template>
    </el-select>
    <el-date-picker
      v-model="dateRange"
      type="daterange"
      start-placeholder="Дата начала"
      end-placeholder="Дата окончания"
      :disabled-date="disabledPastDates"
      range-separator="—"
      unlink-panels
      style="width: 100%; margin-bottom: 12px;"
    />
    <el-button type="primary" @click="applyTemplate">Применить</el-button>
  </el-dialog>
</template>
<script>
import { mapActions, mapState } from 'vuex'
import ShiftCalendar from './ShiftCalendar.vue'
import { generateShiftsFromTemplate } from '@/components/util/scheduleTemplate'

export default {
  name: 'CreateScheduleDialog',
  components: {
    ShiftCalendar
  },
  props: {
    modelValue: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      form: {
        user_id: '',
        location_id: '',
        date: '',
        start_time: '',
        end_time: ''
      },
      userError: null,
      workingHours: { start: '09:00', end: '18:00' },
      generatedShifts: [],
      applyTemplateDialogVisible: false,
      selectedTemplate: null,
      dateRange: []
    }
  },
  computed: {
    ...mapState('users', ['employees']),
    ...mapState('cities', {
      locations: state => {
        const flattened = []
        state.cities.forEach(city => {
          city.children?.forEach(branch => {
            // Add branch entry with room_id: null
            flattened.push({
              id: `branch_${branch.id}`,
              name: `${city.name} / ${branch.name}`,
              city_id: city.id,
              branch_id: branch.id,
              room_id: null,
              working_times: (branch.working_times || branch.working || null)
            })
            branch.children?.forEach(room => {
              flattened.push({
                id: `room_${room.id}`,
                name: `${city.name} / ${branch.name} / ${room.name}`,
                city_id: city.id,
                branch_id: branch.id,
                room_id: room.id,
                working_times: (room.working_times || room.working || null)
              })
            })
          })
        })
        return flattened
      }
    }),
    ...mapState('schedules', {
      templates: state => state.schedulesTemplates,
      schedulesList: state => state.schedules
    }),
    dialogWidth() {
      if (typeof window !== 'undefined' && window.innerWidth <= 480) return 'calc(100vw - 28px)'
      return '490px'
    },
    formLabelWidth() {
      return '88px'
    },
    visible: {
      get() {
        return this.modelValue
      },
      set(val) {
        this.$emit('update:modelValue', val)
      }
    },
    filteredLocations() {
      if (!this.form.user_id) {
        return [];
      }
      const user = this.employees.find(u => u.id === this.form.user_id);
      if (!user || !user.locations || !user.locations.length) {
        return [];
      }
      // Collect allowed locations based on user's allowed locations
      const allowedLocations = [];
      user.locations.forEach(userLoc => {
        if (userLoc.room_id) {
          // Allow only exact room matches
          this.locations.forEach(loc => {
            if (
              loc.city_id === userLoc.city_id &&
              loc.branch_id === userLoc.branch_id &&
              loc.room_id === userLoc.room_id
            ) {
              allowedLocations.push(loc);
            }
          });
        } else if (userLoc.branch_id && userLoc.room_id === null) {
          // Allow only branch entries (room_id null, branch_id match, city_id match)
          this.locations.forEach(loc => {
            if (
              loc.city_id === userLoc.city_id &&
              loc.branch_id === userLoc.branch_id &&
              loc.room_id === null
            ) {
              allowedLocations.push(loc);
            }
          });
        }
      });
      // Remove duplicates (in case user has multiple locations that include same location)
      const uniqueLocations = [];
      const ids = new Set();
      allowedLocations.forEach(loc => {
        if (!ids.has(loc.id)) {
          ids.add(loc.id);
          uniqueLocations.push(loc);
        }
      });

      const userId = this.form.user_id;
      const schedules = this.schedulesList || [];
      const userSchedule = schedules.find(s => String(s.id) === String(userId));
      if (!userSchedule) {
        return uniqueLocations;
      }

      const userKey = `${userSchedule.city_id}_${userSchedule.branch_id}_${userSchedule.room_id ?? 0}`;

      return uniqueLocations.filter(loc => {
        const locKey = `${loc.city_id}_${loc.branch_id}_${loc.room_id ?? 0}`;
        const isUsed = locKey === userKey;
        return !isUsed;
      });
    },
    hasAssignedLocations() {
      const user = this.employees.find(u => u.id === this.form.user_id)
      return Boolean(user?.locations?.some(loc => loc.branch_id || loc.room_id))
    }
  },
  methods: {
    ...mapActions('schedules', ['saveSchedule']),
    createFirstTemplate() {
      this.applyTemplateDialogVisible = false
      this.visible = false
      this.$emit('create-template')
    },
    resetForm() {
      this.form = {
        user_id: '',
        location_id: '',
        date: '',
        start_time: '',
        end_time: ''
      }
      this.userError = ''
      this.workingHours = { start: '09:00', end: '18:00' }
      this.generatedShifts = []
    },
    checkUserAccess(userId, locationId) {
      const user = this.employees.find(u => u.id === userId)
      if (!user) {
        this.$message.error('Сотрудник не найден.');
        return false;
      }
      if (!locationId) {
        return true;
      }
      if (!user.locations || !user.locations.length) return false;
      const selectedLocation = this.locations.find(loc => loc.id === locationId)
      if (!selectedLocation) {
        return false
      }
      for (const loc of user.locations) {
        if (loc.room_id) {
          if (
            loc.room_id === selectedLocation.room_id &&
            loc.branch_id === selectedLocation.branch_id &&
            loc.city_id === selectedLocation.city_id
          ) {
            return true
          }
        } else if (loc.branch_id) {
          if (
            loc.branch_id === selectedLocation.branch_id &&
            loc.city_id === selectedLocation.city_id
          ) {
            return true
          }
        } else if (loc.city_id) {
          this.$message.error('Сотруднику необходимо назначить офис/кабинет')
          return false
        }
      }
      return false
    },
    disabledPastDates(date) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return date < today;
    },
    async submit() {
      if (!this.checkUserAccess(this.form.user_id, this.form.location_id)) return
      await this.$refs.formRef.validate()

      const location = this.locations.find(loc => loc.id === this.form.location_id)
      if (this.form.location_id && !location) {
        this.$message.error('Локация не найдена')
        return
      }
      if (!this.generatedShifts.length) {
        this.$message.warning('Нет смен для сохранения — примените шаблон или выделите дни в календаре')
        return
      }

      const payload = {
        user_id: this.form.user_id,
        city_id: location?.city_id ?? null,
        branch_id: location?.branch_id ?? null,
        room_id: location?.room_id ?? null,
        shifts: this.generatedShifts.map(shift => ({
          date: shift.date,
          start_time: shift.start_time,
          end_time: shift.end_time,
          breaks: Array.isArray(shift.breaks)
            ? shift.breaks.map(b => ({
              type: b.type,
              start: b.start_time,
              end: b.end_time,
              date: shift.date
            }))
            : []
        }))
      }
      try {
        await this.saveSchedule(payload)   // bulk сохранение через стор
        this.$message.success('Смены сохранены')
        this.$emit('saved')
        this.visible = false
        this.resetForm()
      } catch (e) {
        this.$message.error('Ошибка при сохранении смен')
      }
    },
    applyTemplate() {
      const template = this.templates.find(t => t.id === this.selectedTemplate)
      if (!template || !this.dateRange.length) {
        this.$message.error('Выберите шаблон и даты')
        return
      }
      const [startDate, endDate] = this.dateRange
      this.generatedShifts = []
      const shifts = generateShiftsFromTemplate(
        template,
        startDate,
        endDate,
        this.workingHours || { start: '09:00', end: '18:00' }
      )
      this.generatedShifts = [...shifts]
      this.$message.success('Смены сгенерированы')
      this.selectedTemplate = null
      this.dateRange = []
      this.applyTemplateDialogVisible = false
    },
    isLocationUsed(location) {
      const userId = this.form.user_id;
      const existingShifts = this.$store.state.schedules.list || [];
      return existingShifts.some(
        s =>
          String(s.user_id) === String(userId) &&
          s.city_id === location.city_id &&
          s.branch_id === location.branch_id &&
          s.room_id === location.room_id
      );
    },
  },
  watch: {
    modelValue(open) {
      if (open && !this.form.user_id) this.form.user_id = this.$store.state.auth.user?.id || '';
    },
    'form.user_id'(newVal) {
      if (!newVal) {
        this.userError = '';
        return;
      }
      this.form.location_id = '';
      this.userError = '';
      this.workingHours = { start: '09:00', end: '18:00' };
    },
    'form.location_id'(newVal) {
      if (!newVal) {
        this.workingHours = { start: '09:00', end: '18:00' };
        return;
      }
      const location = this.locations.find(loc => loc.id === newVal);
      if (location && location.working_times) {
        this.workingHours = location.working_times;
      } else {
        this.workingHours = { start: '09:00', end: '18:00' };
      }
    }
  },
}
</script>

<style>
.create-schedule-dialog {
  --el-dialog-border-radius: 0px !important;
  background-color: #10244d !important;
  background:
    linear-gradient(rgb(0 108 254 / 22%), rgb(0 108 254 / 22%)),
    linear-gradient(90deg, #030b1b 0%, #15264e 100%) !important;
  background-image:
    linear-gradient(rgb(0 108 254 / 22%), rgb(0 108 254 / 22%)),
    linear-gradient(90deg, #030b1b 0%, #15264e 100%) !important;
  border: 0 !important;
  border-radius: 0 !important;
  box-shadow: 0 22px 56px rgba(0, 6, 19, 0.44) !important;
  font-family: var(--ui-font-family-base);
  width: min(680px, calc(100vw - 40px)) !important;
  max-width: min(680px, calc(100vw - 40px)) !important;
  outline: none !important;
}

.create-schedule-dialog.el-dialog {
  --el-dialog-border-radius: 0px !important;
  max-height: calc(100vh - 32px) !important;
  min-height: 0 !important;
  height: auto !important;
  overflow: visible !important;
  display: block !important;
  margin: 12px auto !important;
  border-radius: 0 !important;
  outline: none !important;
}

body .el-overlay-dialog .create-schedule-dialog.el-dialog,
body .el-dialog.create-schedule-dialog {
  --el-dialog-border-radius: 0px !important;
  width: min(680px, calc(100vw - 40px)) !important;
  max-width: min(680px, calc(100vw - 40px)) !important;
  min-height: 0 !important;
  height: auto !important;
  max-height: calc(100vh - 32px) !important;
  margin: auto !important;
  border: 0 !important;
  border-radius: 0 !important;
  outline: none !important;
  overflow: visible !important;
}

.apply-template-dialog {
  background: var(--appointment-panel);
  border: 1px solid var(--cal-border);
  box-shadow: var(--cal-shadow-card);
  font-family: var(--ui-font-family-base);
}

.create-schedule-dialog .el-dialog__header,
.create-schedule-dialog .el-dialog__body,
.create-schedule-dialog .el-dialog__footer,
.apply-template-dialog .el-dialog__header,
.apply-template-dialog .el-dialog__body,
.apply-template-dialog .el-dialog__footer {
  background: transparent;
  border-radius: 0 !important;
}

.create-schedule-dialog .el-dialog__title,
.apply-template-dialog .el-dialog__title {
  color: var(--cal-text-inverse);
  display: block;
  text-align: center;
}

.create-schedule-dialog .el-dialog__header {
  padding: 14px 18px 4px;
}

.create-schedule-dialog .el-dialog__title {
  font-family: var(--ui-font-family-base);
  font-size: 23px;
  font-weight: 700;
  line-height: 1;
}

.create-schedule-dialog .el-dialog__body {
  padding: 8px 18px 0;
  flex: 0 0 auto !important;
  overflow: visible !important;
  max-height: none !important;
}

body .el-overlay-dialog .create-schedule-dialog.el-dialog .el-dialog__body {
  max-height: none !important;
  overflow: visible !important;
}

.create-schedule-dialog .el-dialog__footer {
  padding: 2px 18px 12px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
}

.create-schedule-dialog .el-dialog__headerbtn,
.apply-template-dialog .el-dialog__headerbtn {
  top: 14px;
  right: 18px;
  color: #031230;
  font-size: 26px;
  font-weight: 700;
}

.create-schedule-dialog .el-form-item__label,
.apply-template-dialog .el-form-item__label {
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  white-space: nowrap;
  font-family: var(--ui-font-family-base);
  font-size: 16px;
  font-weight: 300;
  line-height: 1;
  min-height: 38px;
  padding-right: 8px;
  text-align: left;
}

.create-schedule-dialog .el-form-item__label-wrap {
  margin-right: 8px !important;
}

.create-schedule-dialog .el-input__wrapper,
.create-schedule-dialog .el-select__wrapper,
.create-schedule-dialog .el-date-editor.el-input__wrapper,
.apply-template-dialog .el-input__wrapper,
.apply-template-dialog .el-select__wrapper,
.apply-template-dialog .el-date-editor.el-input__wrapper {
  background: #07142d !important;
  border-radius: 8px !important;
  box-shadow: 0 0 0 1px rgba(48, 87, 154, 0.85) inset !important;
  color: #22c0ff !important;
  border: none !important;
  min-height: 38px !important;
}

.create-schedule-dialog .el-input__wrapper:hover,
.create-schedule-dialog .el-select__wrapper:hover,
.create-schedule-dialog .el-input__wrapper.is-focus,
.create-schedule-dialog .el-select__wrapper.is-focused,
.apply-template-dialog .el-input__wrapper:hover,
.apply-template-dialog .el-select__wrapper:hover {
  background: #0d2757 !important;
  box-shadow: 0 0 0 1px #3f6fb8 inset !important;
}

.create-schedule-dialog .el-input__inner,
.create-schedule-dialog .el-select__placeholder,
.create-schedule-dialog .el-select__selected-item,
.create-schedule-dialog .el-range-input,
.apply-template-dialog .el-input__inner,
.apply-template-dialog .el-select__placeholder,
.apply-template-dialog .el-select__selected-item,
.apply-template-dialog .el-range-input {
  color: #22c0ff !important;
  -webkit-text-fill-color: #22c0ff !important;
  font-family: var(--ui-font-family-base) !important;
  font-size: 15px;
  font-weight: 300;
}

.create-schedule-dialog .el-select__placeholder.is-transparent,
.apply-template-dialog .el-select__placeholder.is-transparent {
  color: #22c0ff !important;
  -webkit-text-fill-color: #22c0ff !important;
}

.create-schedule-dialog .el-select__caret,
.apply-template-dialog .el-select__caret,
.create-schedule-dialog .el-input__icon,
.apply-template-dialog .el-input__icon {
  color: #22c0ff !important;
}

.create-schedule-dialog .el-button--default,
.apply-template-dialog .el-button--default {
  background: #0c224b !important;
  border: none !important;
  box-shadow: 0 0 0 1px rgba(118, 159, 232, 0.25) inset !important;
  color: #ffffff !important;
  min-height: 54px;
  padding-inline: 28px;
}

.create-schedule-dialog .el-button--primary,
.apply-template-dialog .el-button--primary {
  background: #22c0ff !important;
  border-color: transparent !important;
  color: #ffffff !important;
  min-height: 44px;
  padding-inline: 28px;
}

.create-schedule-dialog .el-button--primary.is-plain {
  background: #0c224b !important;
  border: none !important;
  box-shadow: 0 0 0 1px #1b3f7a inset !important;
  color: #22c0ff !important;
}

.create-schedule-dialog .template-actions {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  min-width: 0;
  padding-top: 0;
}

.create-schedule-dialog .el-form-item__content {
  min-width: 0 !important;
  align-items: center !important;
}

.create-schedule-dialog .el-form {
  margin-top: 0;
}

.create-schedule-dialog .el-form-item {
  margin-bottom: 4px;
}

.create-schedule-dialog .shift-calendar,
.create-schedule-dialog .shift-calendar--edit {
  margin-top: 8px;
  --shift-calendar-header-gap: 4px;
  --shift-calendar-header-padding: 1px 0 3px;
  --shift-calendar-title-size: 28px;
  --shift-calendar-nav-gap: 8px;
  --shift-calendar-nav-font-size: 10px;
  --shift-calendar-nav-padding-bottom: 0px;
  --shift-calendar-weekday-font-size: 15px;
  --shift-calendar-weekday-padding-bottom: 4px;
  --shift-calendar-day-height: 30px;
  --shift-calendar-day-font-size: 19px;
  --shift-calendar-day-top-padding: 0px;
  --shift-calendar-day-top-padding-selected: 0px;
  --shift-calendar-selected-size: 26px;
  --shift-calendar-selected-font-size: 15px;
  --shift-calendar-shift-font-size: 7px;
  --shift-calendar-shift-margin-top: 0px;
}

.create-schedule-dialog .template-apply-btn {
  min-width: 162px !important;
  min-height: 34px !important;
  padding: 0 18px !important;
  border-radius: 8px !important;
  background:
    linear-gradient(rgba(4, 35, 83, 0.97), rgba(4, 35, 83, 0.97)),
    linear-gradient(299deg, #030b1b 13.22%, #15264e 86.78%) !important;
  border: none !important;
  box-shadow: 0 0 0 1px rgba(93, 126, 198, 0.18) inset !important;
  color: #22c0ff !important;
  font-size: 15px !important;
  line-height: 1;
  font-weight: 300;
  font-family: var(--ui-font-family-base) !important;
}

.create-schedule-dialog .template-apply-btn:not(.is-disabled):hover,
.create-schedule-dialog .template-apply-btn:not(.is-disabled):focus {
  background: #0d2757 !important;
  box-shadow: 0 0 0 1px #3f6fb8 inset !important;
  color: #22c0ff !important;
}

.create-schedule-dialog .template-apply-btn.is-disabled,
.create-schedule-dialog .template-apply-btn.is-disabled:hover,
.create-schedule-dialog .template-apply-btn.is-disabled:focus {
  background:
    linear-gradient(rgba(4, 35, 83, 0.97), rgba(4, 35, 83, 0.97)),
    linear-gradient(299deg, #030b1b 13.22%, #15264e 86.78%) !important;
  box-shadow: none !important;
  color: rgba(0, 195, 254, 0.72) !important;
  opacity: 1;
}

.create-schedule-dialog .template-apply-btn.is-disabled span,
.create-schedule-dialog .template-apply-btn.is-disabled:hover span,
.create-schedule-dialog .template-apply-btn.is-disabled:focus span {
  color: rgba(0, 195, 254, 0.72) !important;
}

.create-schedule-dialog .el-button--primary.is-disabled,
.create-schedule-dialog .el-button--primary.is-disabled:hover,
.create-schedule-dialog .el-button--primary.is-disabled:focus {
  background: var(--appointment-primary);
  color: var(--appointment-primary-text);
  opacity: 0.55;
}

.create-schedule-dialog .dialog-footer-btn {
  min-width: 132px;
  height: 44px;
  min-height: 44px;
  padding: 0 24px !important;
  border-radius: 12px;
  font-size: 16px;
  line-height: 1;
  font-weight: 700;
  font-family: var(--ui-font-family-base) !important;
}

.create-schedule-dialog .dialog-footer-btn-cancel {
  background: #0c224b !important;
  border: none !important;
  color: #ffffff !important;
  box-shadow: 0 0 0 1px rgba(118, 159, 232, 0.25) inset !important;
}

.create-schedule-dialog .dialog-footer-btn-cancel:hover,
.create-schedule-dialog .dialog-footer-btn-cancel:focus {
  background: #0d2757 !important;
  color: #ffffff !important;
}

.create-schedule-dialog .dialog-footer-btn-submit {
  min-width: 160px;
  background: #22c0ff !important;
  border: none !important;
  color: #ffffff !important;
}

.create-schedule-dialog .dialog-footer-btn-submit:hover,
.create-schedule-dialog .dialog-footer-btn-submit:focus {
  background: #00b0e5 !important;
}

.create-schedule-dialog .el-alert {
  background: rgba(14, 28, 61, 0.92);
  border-color: var(--cal-border);
}

.create-schedule-dialog .el-alert__title,
.create-schedule-dialog .el-link {
  color: var(--cal-text-inverse);
}

@media (max-width: 860px) {
  .create-schedule-dialog {
    width: min(660px, calc(100vw - 28px)) !important;
  }

  .create-schedule-dialog .el-dialog__header,
  .create-schedule-dialog .el-dialog__body,
  .create-schedule-dialog .el-dialog__footer {
    padding-left: 18px;
    padding-right: 18px;
  }
}
@media (min-width: 601px) {
  .create-schedule-dialog .el-form-item__label {
    flex: 0 0 156px !important;
    width: 156px !important;
    min-width: 156px !important;
  }

  .create-schedule-dialog .el-form-item__content {
    flex: 1 1 auto !important;
    margin-left: 0 !important;
  }
}
@media (max-width: 480px) {
  .create-schedule-dialog {
    width: calc(100vw - 24px) !important;
    max-width: calc(100vw - 24px) !important;
    margin: auto !important;
    border-radius: 0 !important;
  }
  .create-schedule-dialog .el-dialog__header {
    padding: 12px 14px 2px !important;
  }
  .create-schedule-dialog .el-dialog__title {
    font-size: 18px !important;
  }
  .create-schedule-dialog .el-dialog__body {
    padding: 8px 14px 2px !important;
    max-height: none !important;
    flex: 0 0 auto !important;
    overflow: visible !important;
  }
  .create-schedule-dialog .el-dialog__footer {
    padding: 4px 14px 8px !important;
    gap: 8px !important;
  }
  .create-schedule-dialog .dialog-footer-btn {
    min-width: 100px !important;
    height: 38px !important;
    min-height: 38px !important;
    padding: 0 16px !important;
    font-size: 13px !important;
  }
  .create-schedule-dialog .el-form-item__label {
    font-size: 12px !important;
    min-height: 34px !important;
    text-align: left !important;
    justify-content: flex-start !important;
    padding-right: 6px !important;
  }
  .create-schedule-dialog .el-form-item__label-wrap {
    margin-right: 6px !important;
  }
  .create-schedule-dialog .el-input__wrapper,
  .create-schedule-dialog .el-select__wrapper {
    min-height: 32px !important;
  }
  .create-schedule-dialog .template-apply-btn {
    min-height: 28px !important;
    font-size: 11px !important;
    padding: 0 12px !important;
  }

  .create-schedule-dialog .shift-calendar,
  .create-schedule-dialog .shift-calendar--edit {
    margin-top: 6px !important;
    --shift-calendar-header-gap: 3px;
    --shift-calendar-header-padding: 0 0 2px;
    --shift-calendar-title-size: 24px;
    --shift-calendar-nav-gap: 6px;
    --shift-calendar-nav-font-size: 9px;
    --shift-calendar-nav-padding-bottom: 0px;
    --shift-calendar-weekday-font-size: 14px;
    --shift-calendar-weekday-padding-bottom: 3px;
    --shift-calendar-day-height: 26px;
    --shift-calendar-day-font-size: 15px;
    --shift-calendar-day-top-padding: 0px;
    --shift-calendar-day-top-padding-selected: 0px;
    --shift-calendar-selected-size: 22px;
    --shift-calendar-selected-font-size: 13px;
    --shift-calendar-shift-font-size: 7px;
    --shift-calendar-shift-margin-top: 0px;
  }
}

/* Calendar sizing inside create dialog */
.create-schedule-dialog .el-calendar-table td {
  padding: 0 !important;
  height: var(--shift-calendar-day-height) !important;
  vertical-align: top !important;
}

.create-schedule-dialog .el-calendar__body,
.create-schedule-dialog .shift-calendar--edit ::v-deep(.el-calendar__body),
.create-schedule-dialog .el-calendar-table,
.create-schedule-dialog .shift-calendar--edit ::v-deep(.el-calendar-table),
.create-schedule-dialog .el-calendar-table tbody,
.create-schedule-dialog .shift-calendar--edit ::v-deep(.el-calendar-table tbody),
.create-schedule-dialog .el-calendar-table tr,
.create-schedule-dialog .shift-calendar--edit ::v-deep(.el-calendar-table tr) {
  height: var(--shift-calendar-day-height) !important;
}

.create-schedule-dialog .el-calendar-day,
.create-schedule-dialog .shift-calendar--edit ::v-deep(.el-calendar-day) {
  height: var(--shift-calendar-day-height) !important;
  min-height: var(--shift-calendar-day-height) !important;
}

.create-schedule-dialog .cell-date,
.create-schedule-dialog .shift-calendar--edit .cell-date {
  font-size: var(--shift-calendar-day-font-size) !important;
  color: #ffffff !important;
  font-weight: 300 !important;
}

.create-schedule-dialog .el-calendar-table td:not(.prev):not(.next) .cell-date {
  color: #ffffff !important;
  font-weight: 300 !important;
}
.create-schedule-dialog .el-calendar-table td.prev .cell-date,
.create-schedule-dialog .el-calendar-table td.next .cell-date {
  color: #000000 !important;
  font-weight: 300 !important;
}
.create-schedule-dialog .el-calendar-table td.is-today .cell-date {
  color: #ffffff !important;
}
.create-schedule-dialog .calendar-date-click-area.selected .cell-date {
  width: var(--shift-calendar-selected-size) !important;
  height: var(--shift-calendar-selected-size) !important;
  line-height: var(--shift-calendar-selected-size) !important;
  font-size: var(--shift-calendar-selected-font-size) !important;
}
.create-schedule-dialog .cell-shift-inline {
  font-size: var(--shift-calendar-shift-font-size) !important;
  margin-top: var(--shift-calendar-shift-margin-top) !important;
}
.create-schedule-dialog .shift-calendar-title,
.create-schedule-dialog .shift-calendar--edit .shift-calendar-title {
  font-size: var(--shift-calendar-title-size) !important;
  color: #ffffff !important;
  line-height: 1 !important;
}
.create-schedule-dialog .el-calendar-table th,
.create-schedule-dialog .shift-calendar--edit ::v-deep(.el-calendar-table th) {
  font-size: var(--shift-calendar-weekday-font-size) !important;
  padding-bottom: var(--shift-calendar-weekday-padding-bottom) !important;
  font-weight: 300 !important;
}
.create-schedule-dialog .shift-calendar-nav,
.create-schedule-dialog .shift-calendar--edit .shift-calendar-nav {
  gap: var(--shift-calendar-nav-gap) !important;
  align-items: center !important;
  padding-bottom: 0 !important;
  flex-wrap: nowrap !important;
  justify-content: flex-end !important;
}

.create-schedule-dialog .shift-calendar-nav-btn,
.create-schedule-dialog .shift-calendar--edit .shift-calendar-nav-btn {
  font-size: var(--shift-calendar-nav-font-size) !important;
  font-weight: 300 !important;
}

.create-schedule-dialog .calendar-date-click-area.disabled {
  opacity: 1 !important;
}

.create-schedule-dialog .el-calendar-table td:not(.prev):not(.next) .calendar-date-click-area.disabled .cell-date,
.create-schedule-dialog .el-calendar-table td.is-today .calendar-date-click-area.disabled .cell-date {
  color: #ffffff !important;
}

.create-schedule-dialog .el-calendar-table td.prev .calendar-date-click-area.disabled .cell-date,
.create-schedule-dialog .el-calendar-table td.next .calendar-date-click-area.disabled .cell-date {
  color: #000000 !important;
}

/* Figma rewrite: create shift modal, node 3:121 */
body .el-overlay-dialog .create-schedule-dialog.el-dialog,
body .el-dialog.create-schedule-dialog,
.create-schedule-dialog.el-dialog {
  --el-dialog-border-radius: 0px !important;
  --el-dialog-padding-primary: 0 !important;
  width: 462px !important;
  max-width: calc(100vw - 28px) !important;
  height: 609px !important;
  max-height: calc(100vh - 24px) !important;
  min-height: 0 !important;
  padding: 0 !important;
  margin: auto !important;
  border: 0 !important;
  border-radius: 0 !important;
  overflow: hidden !important;
  display: flex !important;
  flex-direction: column !important;
  background:
    linear-gradient(rgba(0, 108, 254, 0.22), rgba(0, 108, 254, 0.22)),
    linear-gradient(299deg, #030b1b 13.22%, #15264e 86.78%),
    #334878 !important;
  box-shadow: none !important;
  font-family: "Bahnschrift", var(--ui-font-family-base) !important;
}

body .create-schedule-dialog .el-dialog__header {
  flex: 0 0 auto !important;
  height: 57px !important;
  padding: 24px 46px 0 !important;
  margin: 0 !important;
  background: transparent !important;
  box-sizing: border-box !important;
}

body .create-schedule-dialog .el-dialog__title {
  display: block !important;
  width: 100% !important;
  text-align: center !important;
  color: #ffffff !important;
  font-family: "Bahnschrift", var(--ui-font-family-base) !important;
  font-size: 23px !important;
  font-weight: 700 !important;
  line-height: 1 !important;
  letter-spacing: 0 !important;
}

body .create-schedule-dialog .el-dialog__headerbtn {
  top: 22px !important;
  right: 31px !important;
  width: 22px !important;
  height: 22px !important;
  color: #031230 !important;
  font-size: 24px !important;
}

body .create-schedule-dialog .el-dialog__headerbtn .el-dialog__close,
body .create-schedule-dialog .el-dialog__headerbtn svg,
body .create-schedule-dialog .el-dialog__headerbtn path {
  color: #031230 !important;
  fill: #031230 !important;
  stroke: #031230 !important;
}

body .create-schedule-dialog .el-dialog__body {
  flex: 1 1 auto !important;
  padding: 12px 17px 0 30px !important;
  margin: 0 !important;
  overflow: hidden !important;
  background: transparent !important;
  box-sizing: border-box !important;
}

body .create-schedule-dialog .el-form {
  width: 100% !important;
  margin: 0 !important;
}

body .create-schedule-dialog .el-form-item {
  display: flex !important;
  align-items: center !important;
  margin: 0 0 14px !important;
  height: 22px !important;
}

body .create-schedule-dialog .el-form-item__label-wrap {
  flex: 0 0 72px !important;
  width: 72px !important;
  margin: 0 10px 0 0 !important;
}

body .create-schedule-dialog .el-form-item__label {
  width: 72px !important;
  height: 22px !important;
  min-height: 22px !important;
  padding: 0 !important;
  justify-content: flex-end !important;
  align-items: center !important;
  color: #ffffff !important;
  font-family: "Bahnschrift", var(--ui-font-family-base) !important;
  font-size: 13px !important;
  font-weight: 300 !important;
  line-height: 1 !important;
  text-align: right !important;
  white-space: nowrap !important;
}

body .create-schedule-dialog .el-form-item__content {
  flex: 1 1 auto !important;
  min-width: 0 !important;
  height: 22px !important;
  line-height: 22px !important;
  align-items: center !important;
  margin: 0 !important;
}

body .create-schedule-dialog .el-select,
body .create-schedule-dialog .el-input,
body .create-schedule-dialog .template-actions {
  width: 100% !important;
  height: 22px !important;
}

body .create-schedule-dialog .el-input__wrapper,
body .create-schedule-dialog .el-select__wrapper {
  height: 22px !important;
  min-height: 22px !important;
  padding: 0 11px !important;
  border: 0 !important;
  border-radius: 0 !important;
  background: #06122c !important;
  box-shadow: none !important;
  color: #00c3ff !important;
}

body .create-schedule-dialog .el-input__wrapper:hover,
body .create-schedule-dialog .el-select__wrapper:hover,
body .create-schedule-dialog .el-input__wrapper.is-focus,
body .create-schedule-dialog .el-select__wrapper.is-focused {
  background: #06122c !important;
  box-shadow: none !important;
}

body .create-schedule-dialog .el-input__inner,
body .create-schedule-dialog .el-select__placeholder,
body .create-schedule-dialog .el-select__selected-item {
  color: #00c3ff !important;
  -webkit-text-fill-color: #00c3ff !important;
  font-family: "Bahnschrift", var(--ui-font-family-base) !important;
  font-size: 13px !important;
  font-weight: 300 !important;
  line-height: 22px !important;
}

body .create-schedule-dialog .el-select__placeholder.is-transparent {
  color: #00c3ff !important;
  -webkit-text-fill-color: #00c3ff !important;
}

body .create-schedule-dialog .el-select__caret,
body .create-schedule-dialog .el-input__icon {
  color: #00c3ff !important;
  width: 11px !important;
  height: 11px !important;
}

body .create-schedule-dialog .template-actions {
  display: flex !important;
  align-items: center !important;
  justify-content: flex-start !important;
}

body .create-schedule-dialog .template-apply-btn.el-button {
  width: 166px !important;
  min-width: 166px !important;
  height: 22px !important;
  min-height: 22px !important;
  padding: 0 12px !important;
  border: 0 !important;
  border-radius: 0 !important;
  background: #06122c !important;
  box-shadow: none !important;
  color: #00c3ff !important;
  font-family: "Bahnschrift", var(--ui-font-family-base) !important;
  font-size: 13px !important;
  font-weight: 300 !important;
  line-height: 22px !important;
}

body .create-schedule-dialog .template-apply-btn.el-button span {
  color: #00c3ff !important;
}

body .create-schedule-dialog .template-apply-btn.el-button.is-disabled,
body .create-schedule-dialog .template-apply-btn.el-button.is-disabled:hover {
  background: #06122c !important;
  color: rgba(0, 195, 255, 0.72) !important;
  opacity: 1 !important;
}

body .create-schedule-dialog .employee-error-alert {
  position: absolute !important;
  z-index: 3 !important;
  left: 112px !important;
  top: 24px !important;
  width: 330px !important;
}

body .create-schedule-dialog .shift-calendar,
body .create-schedule-dialog .shift-calendar--edit {
  margin: 42px 0 0 -4px !important;
  width: calc(100% + 2px) !important;
  --shift-calendar-header-gap: 10px;
  --shift-calendar-header-padding: 0 0 7px;
  --shift-calendar-title-size: 24px;
  --shift-calendar-nav-gap: 12px;
  --shift-calendar-nav-font-size: 8px;
  --shift-calendar-nav-padding-bottom: 5px;
  --shift-calendar-weekday-font-size: 27px;
  --shift-calendar-weekday-padding-bottom: 10px;
  --shift-calendar-day-height: 53px;
  --shift-calendar-day-font-size: 27px;
  --shift-calendar-day-top-padding: 1px;
  --shift-calendar-day-top-padding-selected: 0px;
  --shift-calendar-selected-size: 44px;
  --shift-calendar-selected-font-size: 24px;
  --shift-calendar-shift-font-size: 7px;
  --shift-calendar-shift-margin-top: 0px;
}

body .create-schedule-dialog .shift-calendar-header,
body .create-schedule-dialog .shift-calendar--edit .shift-calendar-header {
  display: flex !important;
  align-items: flex-end !important;
  flex-direction: row !important;
  gap: 8px !important;
  width: 100% !important;
}

body .create-schedule-dialog .shift-calendar-title,
body .create-schedule-dialog .shift-calendar--edit .shift-calendar-title {
  flex: 0 0 auto !important;
  color: #ffffff !important;
  font-family: "Bahnschrift", var(--ui-font-family-base) !important;
  font-size: 24px !important;
  font-weight: 700 !important;
  line-height: 1 !important;
  white-space: nowrap !important;
}

body .create-schedule-dialog .shift-calendar-nav,
body .create-schedule-dialog .shift-calendar--edit .shift-calendar-nav {
  display: flex !important;
  flex: 1 1 auto !important;
  justify-content: flex-end !important;
  align-items: center !important;
  gap: 12px !important;
  margin-left: auto !important;
  padding-bottom: 5px !important;
  overflow: visible !important;
  white-space: nowrap !important;
}

body .create-schedule-dialog .shift-calendar-nav-btn,
body .create-schedule-dialog .shift-calendar--edit .shift-calendar-nav-btn {
  flex: 0 0 auto !important;
  color: #ffffff !important;
  font-family: "Bahnschrift", var(--ui-font-family-base) !important;
  font-size: 8px !important;
  font-weight: 300 !important;
  line-height: 1 !important;
  text-decoration: none !important;
}

body .create-schedule-dialog .shift-calendar-nav-btn.is-active,
body .create-schedule-dialog .shift-calendar-nav-btn:hover {
  color: #00c3ff !important;
  text-decoration: underline !important;
  text-underline-offset: 2px !important;
}

body .create-schedule-dialog .el-calendar,
body .create-schedule-dialog .el-calendar__header,
body .create-schedule-dialog .el-calendar__body,
body .create-schedule-dialog .el-calendar-table,
body .create-schedule-dialog .el-calendar-table tbody,
body .create-schedule-dialog .el-calendar-table tr,
body .create-schedule-dialog .el-calendar-table td,
body .create-schedule-dialog .el-calendar-day {
  background: transparent !important;
  border: 0 !important;
}

body .create-schedule-dialog .el-calendar__header {
  padding: 0 0 7px !important;
}

body .create-schedule-dialog .el-calendar__body {
  padding: 0 !important;
}

body .create-schedule-dialog .el-calendar-table th {
  color: #ffffff !important;
  font-family: "Bahnschrift", var(--ui-font-family-base) !important;
  font-size: 27px !important;
  font-weight: 300 !important;
  line-height: 1 !important;
  padding: 0 0 10px !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.16) !important;
}

body .create-schedule-dialog .el-calendar-table td,
body .create-schedule-dialog .el-calendar-table tr,
body .create-schedule-dialog .el-calendar-day {
  height: 53px !important;
  min-height: 53px !important;
  padding: 0 !important;
}

body .create-schedule-dialog .calendar-date-click-area,
body .create-schedule-dialog .calendar-popover-trigger,
body .create-schedule-dialog .calendar-popover-trigger-wrapper {
  height: 53px !important;
  min-height: 53px !important;
  justify-content: flex-start !important;
}

body .create-schedule-dialog .calendar-date-inner {
  padding-top: 0 !important;
}

body .create-schedule-dialog .cell-date {
  color: #ffffff !important;
  font-family: "Bahnschrift", var(--ui-font-family-base) !important;
  font-size: 27px !important;
  font-weight: 300 !important;
  line-height: 1 !important;
}

body .create-schedule-dialog .el-calendar-table td.prev .cell-date,
body .create-schedule-dialog .el-calendar-table td.next .cell-date {
  color: #000713 !important;
  opacity: 1 !important;
}

body .create-schedule-dialog .calendar-date-click-area.selected .cell-date {
  width: 44px !important;
  height: 44px !important;
  min-width: 44px !important;
  line-height: 44px !important;
  border-radius: 50% !important;
  background: #22c0ff !important;
  color: #ffffff !important;
  font-size: 24px !important;
}

body .create-schedule-dialog .cell-shift-inline {
  display: none !important;
}

body .create-schedule-dialog .el-dialog__footer {
  flex: 0 0 auto !important;
  display: flex !important;
  justify-content: flex-end !important;
  align-items: center !important;
  gap: 9px !important;
  padding: 0 23px 19px !important;
  margin: 0 !important;
  background: transparent !important;
  box-sizing: border-box !important;
}

body .create-schedule-dialog .dialog-footer-btn.el-button {
  height: 30px !important;
  min-height: 30px !important;
  padding: 0 16px !important;
  border: 0 !important;
  border-radius: 5px !important;
  font-family: "Bahnschrift", var(--ui-font-family-base) !important;
  font-size: 13px !important;
  font-weight: 700 !important;
  line-height: 30px !important;
}

body .create-schedule-dialog .dialog-footer-btn-cancel.el-button {
  width: 75px !important;
  min-width: 75px !important;
  background: #14387c !important;
  box-shadow: 0 0 0 1px rgba(122, 160, 229, 0.22) inset !important;
  color: #ffffff !important;
}

body .create-schedule-dialog .dialog-footer-btn-submit.el-button {
  width: 87px !important;
  min-width: 87px !important;
  background: #22c0ff !important;
  box-shadow: none !important;
  color: #ffffff !important;
}

@media (max-width: 480px) {
  body .el-overlay-dialog .create-schedule-dialog.el-dialog,
  body .el-dialog.create-schedule-dialog,
  .create-schedule-dialog.el-dialog {
    width: calc(100vw - 28px) !important;
    height: min(609px, calc(100vh - 24px)) !important;
  }

  body .create-schedule-dialog .el-dialog__body {
    padding-left: 20px !important;
    padding-right: 14px !important;
  }

  body .create-schedule-dialog .el-form-item__label-wrap,
  body .create-schedule-dialog .el-form-item__label {
    width: 62px !important;
    flex-basis: 62px !important;
    font-size: 11px !important;
  }

  body .create-schedule-dialog .shift-calendar,
  body .create-schedule-dialog .shift-calendar--edit {
    --shift-calendar-weekday-font-size: 22px;
    --shift-calendar-day-font-size: 23px;
    --shift-calendar-day-height: 47px;
    --shift-calendar-selected-size: 38px;
    --shift-calendar-selected-font-size: 21px;
  }

  body .create-schedule-dialog .el-calendar-table td,
  body .create-schedule-dialog .el-calendar-table tr,
  body .create-schedule-dialog .el-calendar-day,
  body .create-schedule-dialog .calendar-date-click-area,
  body .create-schedule-dialog .calendar-popover-trigger,
  body .create-schedule-dialog .calendar-popover-trigger-wrapper {
    height: 47px !important;
    min-height: 47px !important;
  }
}

@media (max-width: 600px) {
  .create-schedule-dialog .shift-calendar-header,
  .create-schedule-dialog .shift-calendar--edit .shift-calendar-header {
    flex-direction: column !important;
    align-items: flex-start !important;
    gap: 6px !important;
  }
  .create-schedule-dialog .shift-calendar-nav,
  .create-schedule-dialog .shift-calendar--edit .shift-calendar-nav {
    gap: 8px !important;
    flex-wrap: nowrap !important;
    justify-content: flex-end !important;
    width: 100% !important;
    margin-left: 0 !important;
    flex: 0 0 auto !important;
    overflow-x: auto !important;
    overflow-y: hidden !important;
    scrollbar-width: none;
    -ms-overflow-style: none;
    padding-bottom: 2px !important;
  }
  .create-schedule-dialog .shift-calendar-nav::-webkit-scrollbar,
  .create-schedule-dialog .shift-calendar--edit .shift-calendar-nav::-webkit-scrollbar {
    display: none;
  }
  .create-schedule-dialog .shift-calendar-nav-btn,
  .create-schedule-dialog .shift-calendar--edit .shift-calendar-nav-btn {
    flex: 0 0 auto !important;
    font-size: var(--shift-calendar-nav-font-size) !important;
  }
}

@media (max-width: 390px) {
  .create-schedule-dialog .shift-calendar,
  .create-schedule-dialog .shift-calendar--edit {
    --shift-calendar-title-size: 21px;
    --shift-calendar-nav-font-size: 8px;
    --shift-calendar-weekday-font-size: 13px;
    --shift-calendar-day-height: 24px;
    --shift-calendar-day-font-size: 14px;
    --shift-calendar-selected-size: 21px;
    --shift-calendar-selected-font-size: 11px;
  }

  .create-schedule-dialog .shift-calendar-nav-btn,
  .create-schedule-dialog .shift-calendar--edit .shift-calendar-nav-btn {
    font-size: var(--shift-calendar-nav-font-size) !important;
  }
}

/* Keep the rewritten modal stable after legacy responsive overrides. */
body .create-schedule-dialog .shift-calendar,
body .create-schedule-dialog .shift-calendar--edit {
  margin: 42px 0 0 -4px !important;
  --shift-calendar-header-gap: 10px !important;
  --shift-calendar-header-padding: 0 0 7px !important;
  --shift-calendar-title-size: 24px !important;
  --shift-calendar-nav-gap: 12px !important;
  --shift-calendar-nav-font-size: 8px !important;
  --shift-calendar-nav-padding-bottom: 5px !important;
  --shift-calendar-weekday-font-size: 27px !important;
  --shift-calendar-weekday-padding-bottom: 10px !important;
  --shift-calendar-day-height: 53px !important;
  --shift-calendar-day-font-size: 27px !important;
  --shift-calendar-day-top-padding: 1px !important;
  --shift-calendar-day-top-padding-selected: 0px !important;
  --shift-calendar-selected-size: 44px !important;
  --shift-calendar-selected-font-size: 24px !important;
}

body .create-schedule-dialog .shift-calendar-header,
body .create-schedule-dialog .shift-calendar--edit .shift-calendar-header {
  flex-direction: row !important;
  align-items: flex-end !important;
  gap: 8px !important;
}

body .create-schedule-dialog .shift-calendar-nav,
body .create-schedule-dialog .shift-calendar--edit .shift-calendar-nav {
  width: auto !important;
  flex: 1 1 auto !important;
  margin-left: auto !important;
  justify-content: flex-end !important;
  overflow: visible !important;
  gap: 12px !important;
  transform: translateX(18px) !important;
}

body .create-schedule-dialog .shift-calendar-title {
  font-size: 24px !important;
  transform: translateX(-18px) !important;
}

body .create-schedule-dialog .shift-calendar-nav-btn,
body .create-schedule-dialog .shift-calendar--edit .shift-calendar-nav-btn {
  font-size: 8px !important;
  letter-spacing: 0 !important;
}

body .create-schedule-dialog .el-calendar-table th {
  font-size: 27px !important;
}

body .create-schedule-dialog .el-calendar-table td,
body .create-schedule-dialog .el-calendar-table tr,
body .create-schedule-dialog .el-calendar-day,
body .create-schedule-dialog .calendar-date-click-area,
body .create-schedule-dialog .calendar-popover-trigger,
body .create-schedule-dialog .calendar-popover-trigger-wrapper {
  height: 53px !important;
  min-height: 53px !important;
}

body .create-schedule-dialog .cell-date {
  font-size: 27px !important;
}

body .create-schedule-dialog .calendar-date-click-area.selected .cell-date {
  width: 44px !important;
  height: 44px !important;
  line-height: 44px !important;
  font-size: 24px !important;
}

@media (max-width: 600px) {
  body .create-schedule-dialog .shift-calendar-nav,
  body .create-schedule-dialog .shift-calendar--edit .shift-calendar-nav,
  body .create-schedule-dialog .shift-calendar-title {
    transform: none !important;
  }
}

body .el-overlay-dialog .create-schedule-dialog.el-dialog,
body .el-dialog.create-schedule-dialog,
.create-schedule-dialog.el-dialog {
  width: 490px !important;
  border-radius: 5px !important;
}

body .create-schedule-dialog .el-dialog__body {
  padding: 12px 20px 0 10px !important;
}

body .create-schedule-dialog .el-form {
  width: 100% !important;
  margin: 0 !important;
}

body .create-schedule-dialog .el-form-item {
  display: grid !important;
  grid-template-columns: 82px minmax(0, 1fr) !important;
  column-gap: 15px !important;
  align-items: center !important;
  width: 100% !important;
  height: 22px !important;
  margin: 0 0 14px !important;
}

body .create-schedule-dialog .el-form-item__label-wrap {
  grid-column: 1 !important;
  width: 82px !important;
  min-width: 82px !important;
  margin: 0 !important;
}

body .create-schedule-dialog .el-form-item__label {
  width: 82px !important;
  min-width: 82px !important;
  flex: 0 0 82px !important;
  height: 22px !important;
  min-height: 22px !important;
  padding: 0 !important;
  justify-content: flex-end !important;
  text-align: right !important;
  font-size: 10px !important;
  line-height: 1 !important;
}

body .create-schedule-dialog .el-form-item__content {
  grid-column: 2 !important;
  width: 100% !important;
  height: 22px !important;
  min-width: 0 !important;
  margin: 0 !important;
}

body .create-schedule-dialog .el-select,
body .create-schedule-dialog .template-actions {
  width: 100% !important;
}

body .create-schedule-dialog .el-input__wrapper,
body .create-schedule-dialog .el-select__wrapper,
body .create-schedule-dialog .template-apply-btn.el-button {
  border-radius: 4px !important;
}

body .create-schedule-dialog .shift-calendar,
body .create-schedule-dialog .shift-calendar--edit {
  margin: 42px auto 0 !important;
  width: 352px !important;
  max-width: calc(100% - 24px) !important;
  --shift-calendar-weekday-font-size: 25px !important;
  --shift-calendar-day-font-size: 25px !important;
  --shift-calendar-day-height: 50px !important;
}

body .create-schedule-dialog .calendar-date-inner {
  padding-top: 10px !important;
}

body .create-schedule-dialog .calendar-date-click-area.selected .calendar-date-inner {
  padding-top: 5px !important;
}
</style>
<style scoped>
.el-form-item {
  margin-bottom: 16px;
}
.employee-error-alert {
  margin-top: 8px;
}
.template-actions {
  display: flex;
  justify-content: flex-start;
  width: 100%;
}

.template-actions .el-button {
  min-height: 34px;
  padding-inline: 18px;
}
.spacer {
  flex: 1;
}
.calendar-placeholder {
  border: 1px solid var(--ui-color-border-light);
  border-radius: 4px;
  padding: 8px;
}
@media (max-width: 480px) {
  .el-form-item {
    margin-bottom: 8px;
  }
}
</style>
